import { trackStoreVisit } from "../utils/storeVisits";
import React, { useEffect, useState } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStoreBySlug, fetchStoreByDomain } from '../store/reducers/homeReducer';
import { get_cart_products, get_wishlist_products } from '../store/reducers/cardReducer';
import { normalizeStoreName } from '../utils/storeUtils';
import Headers from '../components/Headers';
import Footer from '../components/Footer';
import StoreHeader from '../components/sections/StoreHeader';
import StoreFooter from '../components/sections/StoreFooter';
import api from '../api/api';

const StoreLayout = () => {
    const { storeName, storename } = useParams();
    const effectiveStoreName = storeName || storename;
    const location = useLocation();
    const dispatch = useDispatch();
    const { seller } = useSelector((state) => state.home);
    const { userInfo } = useSelector((state) => state.auth);
    const [headerSettings, setHeaderSettings] = useState(null);
    const [footerSettings, setFooterSettings] = useState(null);
    const [hasCustomHeader, setHasCustomHeader] = useState(false);
    const [hasCustomFooter, setHasCustomFooter] = useState(false);
    // Fetch Cart and Wishlist on Store Change or User Change
    useEffect(() => {
        const params = effectiveStoreName
            ? { userId: userInfo ? userInfo.id : null, storeName: effectiveStoreName }
            : (userInfo ? userInfo.id : null);

        dispatch(get_cart_products(params));
        dispatch(get_wishlist_products(params));
    }, [dispatch, effectiveStoreName, userInfo]);

    // Fetch Store Data
    useEffect(() => {
        const host = window.location.host;
        const isCustomDomain = !host.includes('stores.mypursepay.com') &&
            !host.includes('localhost') &&
            !host.includes('dashboard.mypursepay.com') &&
            !host.includes('merchant.mypursepay.com');

        if (effectiveStoreName && (!seller || seller.shopInfo?.shopName !== effectiveStoreName)) {
            dispatch(fetchStoreBySlug(effectiveStoreName));
        } else if (isCustomDomain && (!seller)) {
            dispatch(fetchStoreByDomain(host));
        }
    }, [effectiveStoreName, seller, dispatch]);


    // Theme Application Logic (Extracted from StoreDetails)
    useEffect(() => {
        if (seller && seller.shopInfo) {
            const themeSettings = seller.themeSettings || seller.shopInfo?.themeSettings;
            const useCustomTheme = themeSettings?.useCustomTheme;

            // Apply light-theme class
            if (useCustomTheme || seller.shopInfo.theme === 'light') {
                document.body.classList.add('light-theme');
            } else {
                document.body.classList.remove('light-theme');
            }

            // Apply Custom CSS Variables
            if (useCustomTheme && themeSettings) {
                const tempRoot = document.body;
                const colors = themeSettings.colors || {};

                const setVar = (name, value) => {
                    if (value) tempRoot.style.setProperty(name, value);
                };

                setVar('--store-bg', colors.background);
                setVar('--store-button-bg', colors.buttonBg);
                setVar('--store-button-text', colors.buttonText);
                setVar('--store-product-card-bg', colors.productCardBg);
                setVar('--store-header-bg', colors.headerBg);
                setVar('--store-footer-bg', colors.footerBg);
                setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
                setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
                setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
                setVar('--store-input-border-width', `${themeSettings.inputs?.borderWidth || 1}px`);
                setVar('--store-input-focus', themeSettings.inputs?.focusRing || colors.accent || '#000000');
                setVar('--store-input-focus-width', `${themeSettings.inputs?.focusWidth || 1}px`);
                setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
                setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
                setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
                setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
                setVar('--store-select-border-width', `${themeSettings.selects?.borderWidth || 1}px`);
                setVar('--store-select-focus', themeSettings.selects?.focusRing || colors.accent || '#000000');
                setVar('--store-select-focus-width', `${themeSettings.selects?.focusWidth || 1}px`);
                setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
                setVar('--store-border', colors.border);
                setVar('--store-muted', colors.muted);
                setVar('--store-glass', colors.glass);

                // Font
                if (themeSettings.typography?.fontFamily) {
                    const fontFamily = themeSettings.typography.fontFamily;
                    setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                    tempRoot.style.fontFamily = `"${fontFamily}", sans-serif`;

                    // Load Font
                    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;
                    if (!document.getElementById(fontId)) {
                        const link = document.createElement('link');
                        link.id = fontId;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                        document.head.appendChild(link);
                    }
                }
            }

        }
        return () => {
            document.body.classList.remove('light-theme');
            document.body.style = ''; // Reset body styles
        }
    }, [seller, effectiveStoreName]);

    useEffect(() => {
        const name = effectiveStoreName || seller?.shopInfo?.shopName;
        if (seller && name) trackStoreVisit(name, location.pathname);
    }, [seller?._id, effectiveStoreName, location.pathname]);

    // Parse Layout for Header/Footer
    useEffect(() => {
        if (seller && seller.layout) {
            const header = seller.layout.find(s => s.type === 'header');
            const footer = seller.layout.find(s => s.type === 'footer');

            if (header) {
                setHeaderSettings(header.settings);
                setHasCustomHeader(true);
            } else {
                setHasCustomHeader(false);
            }

            if (footer) {
                setFooterSettings(footer.settings);
                setHasCustomFooter(true);
            } else {
                setHasCustomFooter(false);
            }
        }
    }, [seller]);

    const isStoreMatching = !effectiveStoreName || !seller?.shopInfo?.shopName ||
        seller.shopInfo.shopName === effectiveStoreName;

    return (
        <div className="min-h-screen bg-[var(--store-bg)] flex flex-col">
            {!isStoreMatching ? (
                <div className="flex-grow flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                </div>
            ) : (
                <>
                    {/* Header */}
                    {hasCustomHeader ? (
                        <StoreHeader settings={headerSettings} storeName={effectiveStoreName} />
                    ) : (
                        <Headers storeName={effectiveStoreName} />
                    )}

                    {/* Main Content */}
                    <main className="flex-grow">
                        <Outlet context={{ isStoreMode: true, storeName: effectiveStoreName }} />
                    </main>

                    {/* Footer */}
                    {hasCustomFooter ? (
                        <StoreFooter settings={footerSettings} storeName={effectiveStoreName} />
                    ) : (
                        <Footer />
                    )}
                </>
            )}
        </div>
    );
};

export default StoreLayout;
